import {
  Disclaimer,
  Div,
  formatDateTimeShort,
  H5,
  lightTheme,
  P,
  Separator,
  SupportTicket,
} from '@vgrubs/components';
import { Fragment } from 'react';
import {
  describeApplied,
  describePriceRequest,
} from '../helpers/menuPriceFixLabels';
import { useMenuPriceFixes } from '../hooks/useMenuPriceFixes';
import { MenuPriceCandidate } from './MenuPriceCandidate';

type Props = {
  ticket: SupportTicket;
  /** Whether this reader may approve: write access, and not somebody else's
   *  ticket. A resolved ticket shows its history only. */
  canApprove: boolean;
  onOpen: (path: string) => void;
};

/**
 * A merchant saying a dish is priced wrong: what they asked for, the dishes on
 * their menus it could be, and a price to approve for each.
 *
 * Nothing on the menu changes until support approves one here. Approving
 * neither resolves the ticket nor writes to the merchant - Reply and Resolve
 * below are still how that happens. Hidden entirely on a ticket that never
 * had a price in it.
 */
export const MenuPriceSection = ({ ticket, canApprove, onOpen }: Props) => {
  const { fixes, applied, busy, approve } = useMenuPriceFixes(ticket.id);

  const resolved = ticket.status === 'resolved';
  const open = resolved ? [] : fixes;
  if (open.length === 0 && applied.length === 0) return null;

  return (
    <>
      <Separator $m="16px 0" />
      <P $m="0">Menu price</P>
      {open.length > 0 && (
        <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
          Check it against the merchant's message above. Approving changes the
          menu but keeps the ticket open.
        </Disclaimer>
      )}

      {open.length > 0 && ticket.status === 'claimed' && !canApprove && (
        <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
          Only {ticket.claimedBy ?? 'the person who claimed it'} can approve a
          price on this ticket.
        </Disclaimer>
      )}

      {open.map((fix, index) => (
        // Positional: a fix has no id, and the list is replaced whole.
        <Fragment key={index}>
          <H5 $m="16px 0 0 0">
            {`"${fix.itemName}": ${describePriceRequest(fix)}`}
          </H5>

          {fix.candidates.length === 0 ? (
            <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
              No matching dish found on the menu.
            </Disclaimer>
          ) : (
            <>
              {fix.candidates.length > 1 && !ticket.order && (
                <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
                  Several dishes match, pick the right brand. Attaching the
                  order narrows this to the menu it came from.
                </Disclaimer>
              )}
              <Div $flex $column $gap="12px" $mt="12">
                {fix.candidates.map((candidate) => (
                  <MenuPriceCandidate
                    /* The live prices are part of the key: a refetch that
                       moves them starts the row over from the new ones. */
                    key={[
                      candidate.itemId,
                      candidate.price,
                      candidate.pickupPrice,
                      candidate.suggestedPrice,
                      candidate.suggestedPickupPrice,
                    ].join('-')}
                    candidate={candidate}
                    canApprove={canApprove}
                    busy={busy}
                    onApprove={approve}
                    onOpen={onOpen}
                  />
                ))}
              </Div>
            </>
          )}
        </Fragment>
      ))}

      {applied.length > 0 && (
        <>
          <P $m="16px 0 4px 0">Approved</P>
          <Div $flex $column $gap="8px">
            {applied.map((entry) => (
              <div key={`${entry.itemId}-${entry.approvedAt}`}>
                <H5 $m="0">{describeApplied(entry)}</H5>
                <Disclaimer $m="2px 0 0 0" $color={lightTheme.lightGray}>
                  {[formatDateTimeShort(entry.approvedAt), entry.publishNote]
                    .filter(Boolean)
                    .join(' · ')}
                </Disclaimer>
              </div>
            ))}
          </Div>
        </>
      )}
    </>
  );
};
